<template>
  <div class="map-chart" :style="{width: config.chartWidth+'px',height:config.chartHeight+'px'}">
    <div class="map-chart-wrapper">
      <img
        v-if="borderImgUrl" class="map-chart-border" :src="borderImgUrl" alt=""
        :style="{
          display:'inline-block',
          width:'100%',
          height: '100%'
        }">
      <div class="map-chart-title" :style="{
        width:'100%',
        textAlign: style.titleStyle.align,
        fontSize: style.titleStyle.fontSize + 'px',
        color: style.titleStyle.color,}">
        {{ config.chartTitle }}
        <el-tooltip v-if="config.chartViewData.showIndicator" class="item" :open-delay="500" effect="dark" placement="top">
          <div slot="content" v-html="indicatorDescription"></div>
          <i class="el-icon-question"></i>
        </el-tooltip>
      </div>
    </div>
    <div ref="mapChart" class="map-chart-container"></div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { COLOR_LIST } from '@/utils/defaultColorData'
import { imgURLtoBase64, randomString, hexToRgba } from '@/utils/common'
import { relateBus } from '@/eventBus'
import { mapBaseOption } from './options'
import ChartMixin from './mixin'

export default {
  mixins: [ChartMixin],

  data () {
    return {
      mapName: '',
    }
  },
  computed: {
    chartStyle () {
      return this.config.chartSet.chartStyle
    },

    columnName () {
      const list = []
      if (this.chartData?.length) {
        const colKeyList = Object.keys(this.chartData[0])
        colKeyList.forEach(item => {
          if (this.chartData[1][item] !== '' && this.chartData[1][item] !== undefined) {
            list.push(this.chartData[0][item])
          }
        })
        return list.slice(1)
      } else {
        return []
      }
    },
    chartData () {
      return this.config.chartViewData.chartData
    },
    renderData () {
      if (this.chartData?.length && Array.isArray(this.chartData)) {
        const list = []
        this.chartData.slice(1).forEach(item => {
          const value = []
          if (item.x) {
            let curName
            if (this.config.chartSet.chartStyle.map.length === 1) {
              curName = this.mapNameList.find(bar => item.x.indexOf(bar) !== -1)
            }
            list.push({
              name: curName || item.x,
              value: [item.y_0, item.label],
            })
          } else {
            this.columnName.forEach((name, index) => {
              value.push(item[`col_${index + 1}`])
            })
            list.push({
              name: item.col_0 || item.x,
              value,
            })
          }
        })
        return list.length ? list : []
      } else {
        return []
      }
    },
    borderStyle () {
      return this.config.chartSet.borderStyle.borderTemplate
    },
    style () {
      return this.config.chartSet
    },
    chartSet () {
      return this.config.chartSet
    },
  },
  watch: {
    // 配置项变更
    chartSet: {
      handler: function (val) {
        this.init()
      },
      deep: true,
    },
    'config.chartViewData.chartData': {
      async handler () {
        this.init()
      },
    },
    'config.chartSet.chartStyle.map': {
      async handler () {
        await this.getMap()
        this.init()
      },
    },
    'config.chartWidth': {
      async handler () {
        this.init()
      },
    },
    'config.chartHeight': {
      async handler () {
        this.init()
      },
    },

    // 全局样式监听
    'globalConfig.chartColors': {
      handler () {
        this.init()
      },
      deep: true,
    },
    async borderStyle (val) {
      if (val) {
        this.borderImgUrl = await imgURLtoBase64(val)
      } else {
        this.borderImgUrl = ''
      }
    },
  },

  async created () {
    if (this.borderStyle) {
      this.borderImgUrl = await imgURLtoBase64(this.borderStyle)
    }
    //  新增地图缩放、轮播延时
    if (!this.chartStyle?.zoom) { this.$set(this.config.chartSet.chartStyle, 'zoom', 1) }
    if (!this.style.chartStyle?.carouselDelay) { this.$set(this.config.chartSet.chartStyle, 'carouselDelay', 3) }
  },
  async mounted () {
    if (!this.charts) {
      this.charts = echarts.init(this.$refs.mapChart)
    }
    await this.getMap()
    this.init(this.showData)
  },
  updated (val) {
  },
  methods: {
    async getMap () {
      // 注册地图类型
      const mapRes = await fetch(`https://kylin-static.oss-cn-hangzhou.aliyuncs.com/common/geojson/${this.config.chartSet.chartStyle.map.join('/')}.json`)
      const mapJson = await mapRes.json()
      this.mapName = randomString()
      echarts.registerMap(this.mapName, mapJson)
      if (this.config.chartSet.chartStyle?.map.length === 1 && !this.mapNameList?.length) { // 长度为1 表示是全国地图
        this.mapNameList = mapJson.features.map(bar => {
          return bar.properties.name
        })
      }
    },

    async init () {
      if (!this.mapName) return
      this.option = _.merge(_.cloneDeep(mapBaseOption), {
        tooltip: {
          textStyle: {
            fontSize: this.chartStyle.fontSize,
          },
          formatter: (params) => {
            if (params.data) {
              let str = `${params.data.name}  <br>`
              this.columnName.forEach((item, index) => {
                str += `${item}:  ${params.data.value[index] || 0}<br>`
              })
              return str
            }
            return params.name
          },
        },
        series: [
          { data: this.renderData },
        ],
        geo: {
          zoom: this.chartStyle.zoom,
          label: {
            show: this.chartStyle.isLabel,
            fontSize: this.chartStyle.fontSize,
          },
          map: this.mapName,
          emphasis: {
            label: {
              fontSize: this.chartStyle.fontSize,
            },
            itemStyle: {
              areaColor: COLOR_LIST.filter(item => { return item.label === this.globalConfig.chartColors })[0].value[0], // 鼠标滑过区域颜色
            },
          },
          itemStyle: {
            borderWidth: 1, // 边际线大小
            borderColor: hexToRgba(COLOR_LIST.filter(item => { return item.label === this.globalConfig.chartColors })[0].value[0], 0.8), // 边界线颜色
            areaColor: COLOR_LIST.filter(item => { return item.label === this.globalConfig.chartColors })[0].value[0],

            opacity: 0.3,
          },
          regions: this.renderData.map(bar => {
            return {
              name: bar.name,
              itemStyle: {
                show: true,
                areaColor: COLOR_LIST.filter(item => { return item.label === this.globalConfig.chartColors })[0].value[0],
                opacity: 0.6,
              },
            }
          }),
        },
      })

      this.$nextTick(() => {
        this.charts.setOption(this.option, true)
        this.charts.resize()
      })

      if (this.chartStyle.carousel) {
        this.mouseEvents()
        this.tooltipsActive()
      } else {
        if (this.timer) {
          clearInterval(this.timer)
          this.index = -1
        }
      }
    },

    // 提示轮播
    tooltipsActive () {
      if (this.timer) {
        clearInterval(this.timer)
      }
      const dataLength = this.renderData.length
      // 用定时器控制高亮
      this.timer = setInterval(() => {
        // 清除之前的高亮
        this.charts.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        if (this.index === dataLength - 1) {
          this.index = 0
        } else {
          this.index++
        }
        this.charts.setOption(this.option, true)

        // 当前下标高亮
        this.charts.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        this.charts.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: this.index,
        })
        // 联动 eventBus 事件触发
        relateBus.$emit('activeChange', this.config.addSn, this.renderData[this.index])
      }, this.config.chartSet.chartStyle.carouselDelay * 1000)
    },

  },
}
</script>

<style lang='scss' scoped>
.map-chart{
  height:100%;
  display: flex;
  flex-direction: column;
  &-wrapper {
    position: relative;
    width:100%;
    margin-bottom: 10px;
  }
  &-border {
    position: absolute;
    top: 0;
    height: 100%;
    z-index: -1;

  }
  &-title {
    padding:7px 30px;
  }

  .map-chart-container{
    flex: 1;
  }
}
</style>
